/**
 * Lightweight Recommendation Layer — pure read-only helpers that surface
 * obvious, high-confidence suggestions at the moment of a scheduling action.
 *
 * NOT AI. NOT a new engine. NO scoring system, NO weights, NO persistence.
 *
 * All inputs are already-computed:
 *   • CapacityGrid (jobFitMap, rows[].cells[].state)
 *   • riskSignals (getJobRiskSignals, countCrewRiskyJobs)
 *
 * Source-of-truth rules:
 *   Better Crew → another crew has fitScore ≥ current + 15, has an "open"
 *                 cell on the same date, and is NOT itself high-risk-loaded.
 *   Risk-aware  → job has ≥1 risk signal (rescheduled ≥2, extended, conflicted).
 *   Crew Load   → currently selected crew already carries ≥2 risky jobs.
 *
 * Travel suggestions: intentionally NOT implemented — no clean signal today.
 */
import type { Job } from "@/data/types";
import type { CapacityGrid } from "./capacityEngine";
import type { SchedulingEvent, SchedulingAuditEntry } from "./eventBus";
import { getJobRiskSignals, countCrewRiskyJobs } from "./riskSignals";

/** Minimum fit improvement (0-100 scale) before we suggest swapping crews. */
export const BETTER_CREW_DELTA = 15;
/** Threshold at which a crew is considered "high-risk loaded". */
export const HIGH_RISK_LOAD_THRESHOLD = 2;

export type SuggestionKind = "better_crew" | "risk_aware" | "crew_load";

export interface SchedulingSuggestion {
  kind: SuggestionKind;
  /** Single short line shown to the operator. */
  message: string;
  /** Subtle visual tone. */
  tone: "info" | "warning";
  /** Better Crew only: the crew the operator can switch to. */
  proposedCrew?: string;
  /** Better Crew only: numeric current vs proposed, for display. */
  fitFrom?: number;
  fitTo?: number;
}

interface BuildOpts {
  job: Job;
  /** The crew currently shown / about to be confirmed for this job. */
  currentCrew: string | null | undefined;
  /** The date the job is (or will be) scheduled on. */
  dateIso: string | null | undefined;
  grid: CapacityGrid;
  jobs: Job[];
  eventLog: SchedulingEvent[];
  auditTrail: SchedulingAuditEntry[];
  /** Surfaces apply at different sites — popover tolerates 1, inspector 2. */
  max: 1 | 2;
  /**
   * If true, evaluate Crew Load on the currentCrew. Inspector turns this on;
   * popover leaves it off (the cell already has its crew assigned and the
   * popover surface is meant to stay minimal).
   */
  includeCrewLoad?: boolean;
}

/**
 * Returns up to `max` suggestions in priority order:
 *   1. Better Crew     (info)
 *   2. Risk-aware      (warning, informational only)
 *   3. Crew Load       (warning, inspector only)
 */
export function buildSchedulingSuggestions(opts: BuildOpts): SchedulingSuggestion[] {
  const { job, currentCrew, dateIso, grid, jobs, eventLog, auditTrail, max, includeCrewLoad } = opts;
  const out: SchedulingSuggestion[] = [];

  // ── 1. Better Crew ────────────────────────────────────────────────
  // Read sibling-crew fit from the already-computed jobFitMap. Compare
  // against the current crew's fit; require the candidate to have an
  // "open" cell on the same date AND to not be high-risk-loaded itself.
  const fitMap = grid.jobFitMap.get(job.id);
  if (fitMap && currentCrew && dateIso) {
    const currentFit = fitMap.get(currentCrew) ?? null;
    if (currentFit != null) {
      let best: { crew: string; fit: number } | null = null;
      for (const row of grid.rows) {
        if (row.crewName === currentCrew) continue;
        const candidateFit = fitMap.get(row.crewName);
        if (candidateFit == null) continue;
        if (candidateFit < currentFit + BETTER_CREW_DELTA) continue;
        // Same-date availability check — read-only on the grid.
        const cell = row.cells.find(c => c.dateIso === dateIso);
        if (!cell || cell.state !== "open") continue;
        // Don't recommend a crew that's itself high-risk-loaded.
        const scheduled = jobs.filter(j => j.id !== job.id && !!j.scheduledInstallDate);
        const candidateRisk = countCrewRiskyJobs(row.crewName, scheduled, eventLog, auditTrail);
        if (candidateRisk >= HIGH_RISK_LOAD_THRESHOLD) continue;
        if (!best || candidateFit > best.fit) {
          best = { crew: row.crewName, fit: candidateFit };
        }
      }
      if (best) {
        out.push({
          kind: "better_crew",
          tone: "info",
          message: `Better match available: ${best.crew} (${best.fit} vs ${currentFit})`,
          proposedCrew: best.crew,
          fitFrom: currentFit,
          fitTo: best.fit,
        });
      }
    }
  }

  // ── 2. Risk-aware ────────────────────────────────────────────────
  if (out.length < max) {
    const risk = getJobRiskSignals(job.id, eventLog, auditTrail);
    if (risk.hasAny) {
      out.push({
        kind: "risk_aware",
        tone: "warning",
        message: "Consider alternate crew — this job has prior scheduling issues",
      });
    }
  }

  // ── 3. Crew Load (Inspector only) ────────────────────────────────
  if (includeCrewLoad && out.length < max && currentCrew) {
    const scheduled = jobs.filter(j => j.id !== job.id && !!j.scheduledInstallDate);
    const count = countCrewRiskyJobs(currentCrew, scheduled, eventLog, auditTrail);
    if (count >= HIGH_RISK_LOAD_THRESHOLD) {
      out.push({
        kind: "crew_load",
        tone: "warning",
        message: `Crew has ${count} high-risk jobs scheduled this week`,
      });
    }
  }

  return out.slice(0, max);
}
